// Relative path: src/components/AiCommentaryRenderer.js

import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import InfoTooltip from './InfoTooltip';
import BarGraph from './BarGraph';
import ReviewBlock from './ReviewBlock';
import aiStarsIcon from '../images/ai_stars.svg';
import recUtils from '../utils/RecUtils';
import websiteUtils from '../utils/WebsiteUtils';

// Text processing cache to avoid expensive regex operations
const textProcessingCache = new Map();

function getCacheKey(text, baseKeywords, alwaysBoldList) {
  return `${text}|${baseKeywords.join(',')}|${alwaysBoldList.join(',')}`;
}

/**
 * Reusable BoxedSection component for consistent styling across AI commentary sections
 */
const BoxedSection = React.memo(({ title, children, className = "" }) => {
  return (
    <div className={`mt-6 ${className}`}>
      <div className="bg-transparent border-2 border-gray-200 rounded-lg w-full px-4 md:px-8 py-5 text-gray-700">
        {title && (
          <>
            <h3 className="flex items-center justify-start text-center text-2xl md:text-2xl font-semibold mb-4">
              <img src={aiStarsIcon} alt="" className="w-7 h-7 mr-2 inline-block" />
              {title}
            </h3>
            <div className="mb-4 border-t-2 border-gray-200 border-opacity-100"></div>
          </>
        )}
        {children}
      </div>
    </div>
  );
});

/**
 * Satisfaction comparison graph that animates when visible
 */
const SatisfactionGraph = React.memo(({ clinicName, clinicSatisfaction = 4.5, avgSatisfaction = 3.2 }) => {
  // Determine if we should start scale from 2 or 0
  const minDomain = (clinicSatisfaction > 2 && avgSatisfaction > 2) ? 2 : 0;

  const data = useMemo(() => [
    {
      name: clinicName,
      value: clinicSatisfaction,
      color: "#3B82F6" // mm_green color for this clinic
    },
    {
      name: "All Clinics Avg.",
      value: avgSatisfaction,
      color: "#9CA3AF" // Gray color for all clinics
    }
  ], [clinicName, clinicSatisfaction, avgSatisfaction]);

  return (
    <div className="mt-4 mb-6">
      <BarGraph
        title="Satisfaction Comparison"
        subtitle="Patients similar to your profile"
        data={data}
        minValue={minDomain}
        maxValue={5}
        showScale={true}
        animationDelay={0.1}
      />
    </div>
  );
});

function formatTimestamp(timestampValue) {
  let date;
  if (timestampValue && typeof timestampValue.toDate === 'function') {
    date = timestampValue.toDate();
  } else {
    date = new Date(timestampValue);
  }
  return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
}

function mergeQuotesWithSentiment(quotesArray, sentiment) {
  return quotesArray.map(q => ({ text: q, sentiment }));
}

function alwaysBoldPass(text, alwaysBoldList) {
  let output = text;
  alwaysBoldList.forEach(term => {
    if (!term || !term.trim()) return;
    const escaped = term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const pattern = new RegExp(`(${escaped})`, 'gi');
    output = output.replace(pattern, '<strong>$1</strong>');
  });
  return output;
}

function baseKeywordsPass(text, baseKeywords) {
  let output = text;
  baseKeywords.forEach(base => {
    const pattern = new RegExp(`\\b(${base}[a-z]*)`, 'gi');
    output = output.replace(pattern, (match, p1, offset, fullStr) => {
      const before = fullStr.slice(0, offset);
      const openIndex = before.lastIndexOf('<strong>');
      const closeIndex = before.lastIndexOf('</strong>');
      if (openIndex > closeIndex) {
        return match;
      }
      return `<strong>${match}</strong>`;
    });
  });
  return output;
}

// Optimized text highlighting with caching
function doFullHighlight(originalText, baseKeywords, alwaysBoldList) {
  if (!originalText) return '';
  
  const cacheKey = getCacheKey(originalText, baseKeywords, alwaysBoldList);
  
  // Return cached result if available
  if (textProcessingCache.has(cacheKey)) {
    return textProcessingCache.get(cacheKey);
  }
  
  // Process text and cache result
  let stage1 = alwaysBoldPass(originalText, alwaysBoldList);
  let stage2 = baseKeywordsPass(stage1, baseKeywords);
  
  // Cache with size limit to prevent memory leaks
  if (textProcessingCache.size > 100) {
    const firstKey = textProcessingCache.keys().next().value;
    textProcessingCache.delete(firstKey);
  }
  
  textProcessingCache.set(cacheKey, stage2);
  return stage2;
}

function reorderQuotesBasedOnSatisfaction(posItems, negItems, satisfactionLevel) {
  const level = typeof satisfactionLevel === 'string' ? parseInt(satisfactionLevel, 10) : satisfactionLevel;
  if (level === 5 || level === 4 || level < 0) {
    return [...posItems, ...negItems];
  } else if (level === 3) {
    return [...negItems, ...posItems];
  } else {
    return [...negItems, ...posItems];
  }
}

const baseHighlightKeys = [
  'happy','extreme','dissatisf','unsatisf','satisf','disappoint','botch','ruin','very','good','bad',
  'similar','match','clean','concern','finasteride','minoxidil','dutasteride','medication','result','surgeon','doctor','pain'
];

/**
 * Renders HTML with keyword highlighting.
 * `extraClasses` lets us add indentation or color overrides.
 * Memoized to prevent unnecessary re-processing.
 */
const RenderGeneratedText = React.memo(({ rawText, alwaysBoldList, extraClasses = '' }) => {
  const finalHtml = useMemo(() => {
    if (!rawText) return '';
    return doFullHighlight(rawText, baseHighlightKeys, alwaysBoldList);
  }, [rawText, alwaysBoldList]);

  if (!finalHtml) return null;
  
  return (
    <p
      className={`text-gray-800 ${extraClasses}`}
      dangerouslySetInnerHTML={{ __html: finalHtml }}
    />
  );
});

export default function AiCommentaryRenderer({
  selectedClinic,
  aiCommentary,
  reviewerMetadata,
  formData,
  satisfactionData = { clinicSatisfaction: 4.5, avgSatisfaction: 3.2 }
}) {
  // State for dynamic titles
  const [dynamicTitles, setDynamicTitles] = useState([]);
  const [titlesLoading, setTitlesLoading] = useState(true);
  const [titlesError, setTitlesError] = useState(null);

  // Move useEffect before the conditional return to follow React Hook rules
  useEffect(() => {
    // Only execute cleanup if aiCommentary exists
    return () => {
      if (aiCommentary) {
        Object.keys(aiCommentary).forEach(key => {
          if (key.startsWith('ref_') && key.endsWith('_review_id')) {
            const reviewId = aiCommentary[key];
            if (reviewId) {
              recUtils.releaseProfilePicture(reviewId);
            }
          }
        });
      }
    };
  }, [aiCommentary]);

  // Generate dynamic titles when component mounts or dependencies change
  useEffect(() => {
    if (!aiCommentary || !selectedClinic || !formData) {
      setTitlesLoading(false);
      return;
    }

    const generateTitles = async () => {
      setTitlesLoading(true);
      setTitlesError(null);
      
      try {
        // console.log('Generating dynamic titles for clinic:', selectedClinic);
        const titles = await recUtils.generateDynamicTitles(
          aiCommentary, 
          reviewerMetadata, 
          formData, 
          selectedClinic
        );
        // console.log('DYNAMIC TITLES: All available aiCommentary keys:', Object.keys(aiCommentary).sort());
        
        // console.log('Generated dynamic titles:', titles);
        setDynamicTitles(titles);
      } catch (error) {
        console.error('Error generating dynamic titles:', error);
        setTitlesError(error.message);
        
        // Generate fallback titles
        const refIndices = new Set();
        Object.keys(aiCommentary).forEach(key => {
          if (key.startsWith('ref_') && key.includes('_')) {
            const parts = key.split('_');
            if (parts.length >= 2) refIndices.add(parts[1]);
          }
        });
        const fallbackTitles = [...refIndices].sort().map((_, index) => `Reviewer Reference ${index + 1}`);
        setDynamicTitles(fallbackTitles);
      } finally {
        setTitlesLoading(false);
      }
    };

    generateTitles();
  }, [aiCommentary, reviewerMetadata, formData, selectedClinic]);

  // Memoize expensive computations
  const { refIndices, alwaysBoldList, introBlock } = useMemo(() => {
    if (!aiCommentary) return { refIndices: new Set(), alwaysBoldList: [], introBlock: null };

    // Collect all ref indices
    const indices = new Set();
    Object.keys(aiCommentary).forEach(key => {
      if (key.startsWith('ref_') && key.includes('_')) {
        const parts = key.split('_');
        if (parts.length >= 2) indices.add(parts[1]);
      }
    });

    // Build always-bold list from clinic + reviewer names
    const allReviewerNames = [];
    [...indices].forEach(idx => {
      const name = aiCommentary[`ref_${idx}_reviewer_name`];
      if (name && name.trim()) allReviewerNames.push(name.trim());
    });
    const boldList = [selectedClinic, ...allReviewerNames];

    // ICON-INDENTED CLINIC INTRO
    const intro = aiCommentary.clinic_intro ? (
      <div className="relative mb-6">
        <RenderGeneratedText
          rawText={aiCommentary.clinic_intro}
          alwaysBoldList={boldList}
          extraClasses="text-[15px] leading-[20px] md:text-[16px] md:leading-[22px]"
        />
      </div>
    ) : null;

    return { refIndices: indices, alwaysBoldList: boldList, introBlock: intro };
  }, [aiCommentary, selectedClinic]);

  // Create sorted reviewer data - positive satisfaction first, then negative
  const sortedReviewerData = useMemo(() => {
    if (!aiCommentary || !reviewerMetadata || refIndices.size === 0) return [];
    
    const reviewerData = [...refIndices].sort().map((idx, originalIndex) => {
      const reviewId = aiCommentary[`ref_${idx}_review_id`];
      const data = reviewerMetadata[reviewId] || {};
      const satisfaction = data.user_satisfaction || -1;
      const satisfactionNum = typeof satisfaction === 'string' ? parseFloat(satisfaction) : satisfaction;
      const isPositive = satisfactionNum > 3;
      
      return {
        idx,
        originalIndex, // Keep track of original position for dynamic titles
        satisfaction: satisfactionNum,
        isPositive,
        reviewId,
        data
      };
    });
    
    // Sort by satisfaction: positive first (> 3), then negative (<= 3)
    // Within each group, maintain original order by idx
    reviewerData.sort((a, b) => {
      if (a.isPositive !== b.isPositive) {
        return b.isPositive - a.isPositive; // positive (true/1) comes before negative (false/0)
      }
      // Within same satisfaction group, maintain original order
      return parseInt(a.idx) - parseInt(b.idx);
    });
    
    return reviewerData;
  }, [aiCommentary, reviewerMetadata, refIndices]);

  if (!aiCommentary) return null;

  return (
    <div>
      <h2 className="my-6 font-bold text-gray-700 text-2xl">
        Personalized AI Research
      </h2>

      {/* Clinic Analysis Section */}
      {(introBlock || satisfactionData) && (
        <BoxedSection title="Clinic Analysis">
          {introBlock}
          <SatisfactionGraph 
            clinicName={selectedClinic}
            clinicSatisfaction={satisfactionData.clinicSatisfaction} 
            avgSatisfaction={satisfactionData.avgSatisfaction} 
          />
        </BoxedSection>
      )}

      {/* Reviewer Reference Sections - Now sorted by satisfaction level */}
      {sortedReviewerData.map((reviewerInfo, displayIndex) => {
        const { idx, originalIndex, data } = reviewerInfo;
        const reviewId = aiCommentary[`ref_${idx}_review_id`];
        const reviewerName = aiCommentary[`ref_${idx}_reviewer_name`] || data.reviewer_name || 'Unknown';
        const satisfactionLevel = data.user_satisfaction || -1;
        
        // Use centralized method for website source info
        const websiteInfo = websiteUtils.getWebsiteSourceInfo(data);
        const externalUrl = websiteInfo.external_url;
        const websiteSource = websiteInfo.displayName;
        const siteLogo = websiteInfo.siteLogo;
        
        const authorImg = data.authorImg || null;
        const operationDate = data.timestamp ? formatTimestamp(data.timestamp) : "";

        // Use common method for dynamic labels
        const reviewerLabels = (data.labels || []).map(l => l.toLowerCase());
        const dynamicLabels = recUtils.generateDynamicLabels(formData, reviewerLabels);

        // Combine intro + opinion
        const refIntro = aiCommentary[`ref_${idx}_intro`] || '';
        const refOpinion = aiCommentary[`ref_${idx}_opinion`] || '';
        const combinedCommentary = [refIntro, refOpinion].filter(Boolean).join(' ');

        // ICON-INDENTED REF INTRO
        const commentaryBlock = combinedCommentary.trim() ? (
          <div className="relative mb-3">
            <RenderGeneratedText
              rawText={combinedCommentary}
              alwaysBoldList={alwaysBoldList}
              extraClasses="text-[15px] leading-[20px] md:text-[16px] md:leading-[22px]"
            />
          </div>
        ) : null;

        // Fetch and reorder quotes
        const posArr = aiCommentary[`ref_${idx}_quotes_positive`] || [];
        const negArr = aiCommentary[`ref_${idx}_quotes_negative`] || [];
        const posItems = mergeQuotesWithSentiment(posArr, 'positive');
        const negItems = mergeQuotesWithSentiment(negArr, 'negative');
        const displayedQuotes = reorderQuotesBasedOnSatisfaction(posItems, negItems, satisfactionLevel);

        // Determine the title to use - use originalIndex to get correct dynamic title
        let sectionTitle;
        if (titlesLoading) {
          sectionTitle = `Loading...`;
        } else if (titlesError) {
          sectionTitle = `Reviewer Reference ${originalIndex + 1}`;
        } else {
          // Use dynamic title based on original position, not display position
          sectionTitle = dynamicTitles[originalIndex] || `Reviewer Reference ${originalIndex + 1}`;
        }

        return (
          <BoxedSection key={`ref-section-${idx}`} title={sectionTitle}>
            {commentaryBlock}

            {displayedQuotes.length > 0 && (
              <ReviewBlock
                reviewId={reviewId}
                reviewerName={reviewerName}
                satisfactionLevel={satisfactionLevel}
                externalUrl={externalUrl}
                websiteSource={websiteSource}
                siteLogo={siteLogo}
                authorImg={authorImg}
                operationDate={operationDate}
                dynamicLabels={dynamicLabels}
                displayedQuotes={displayedQuotes}
                virtualizeRendering={true}
                isExternal={websiteInfo.isExternal}
              />
            )}
          </BoxedSection>
        );
      })}

      {/* SUMMARY BUBBLE - Keep existing styling */}
      {aiCommentary.clinic_summary && (
        <div className="mt-6">
          <div className="bg-transparent border-2 border-gray-200 rounded-lg w-full px-4 md:px-8 py-5 text-gray-700">
            
            <h3 className="flex items-center justify-start text-center text-2xl md:text-2xl font-semibold mb-4">
              <img src={aiStarsIcon} alt="" className="w-7 h-7 mr-1 inline-block" />
              AI Report Summary:
            </h3>
            <div className="my-4 border-t-2 border-gray-200 border-opacity-100"></div>
            <RenderGeneratedText
              rawText={aiCommentary.clinic_summary}
              alwaysBoldList={alwaysBoldList}
              extraClasses="mx-1 text-[16px] leading-[22px] text-gray-700"
            />
          </div>
        </div>
      )}
    </div>
  );
}